import { Box, Tooltip, Typography } from '@mui/material'
import { type TagDefinition, resolveTagColor } from '@shared/tags/tagRegistry'
import React from 'react'

interface TagDotRowProps {
  tagIds: string[]
  tagRegistry: Record<string, TagDefinition>
  tagColorMap?: Record<string, string>
  /** Maximum dots rendered before collapsing to a `+N` counter. */
  max?: number
  size?: 'small' | 'medium'
}

/**
 * Compact colored dots for a tag list, with a tooltip listing all names.
 * Renders nothing when tagIds is empty.
 */
export function TagDotRow({ tagIds, tagRegistry, tagColorMap = {}, max = 3, size = 'small' }: TagDotRowProps) {
  if (tagIds.length === 0) {
    return null
  }
  const shown = tagIds.slice(0, max)
  const overflow = tagIds.length - shown.length
  const px = size === 'small' ? 7 : 9

  const names = tagIds.map((id) => tagRegistry[id]?.name ?? id)

  return (
    <Tooltip title={names.join(' · ')} arrow placement="top" disableInteractive>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
        {shown.map((id) => (
          <Box
            key={id}
            sx={{
              width: px,
              height: px,
              borderRadius: '50%',
              backgroundColor: resolveTagColor(id, tagColorMap, tagRegistry),
            }}
          />
        ))}
        {overflow > 0 && (
          <Typography component="span" sx={{ fontSize: '0.6rem', opacity: 0.7, ml: 0.25 }}>
            +{overflow}
          </Typography>
        )}
      </Box>
    </Tooltip>
  )
}
